import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Accordion, AccordionSummary, AccordionDetails, Box, Checkbox, Typography, } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CommonLoader from '../../../common_components/CommonLoader';
import FormattedMeetingText from './FormattedMeetingText';
// Meeting times are pinned to US Eastern like the rest of the portal
// (Hubspot users panel, sales reporting), so everyone reads the same clock
// regardless of the viewer's machine timezone.
const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
});
const fmtDateTime = (iso) => {
    if (!iso)
        return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime()))
        return '—';
    return `${DATE_FORMATTER.format(d)} EST`;
};
const SectionLabel = ({ children }) => (_jsx(Typography, { sx: { fontSize: '13px !important', fontWeight: 600, color: '#344054', mb: 0.25 }, children: children }));
/**
 * Participants as a numbered list flowing down two columns:
 *
 *   1) a@x.com    4) d@x.com
 *   2) b@x.com    5) e@x.com
 *   3) c@x.com    6) f@x.com
 *
 * CSS multi-column fills column-first, which is exactly that order. A counter
 * renders "1)" — the browser's own decimal marker gives "1." and, in a
 * multi-column list, sits outside the column box where it can clip.
 */
const ParticipantList = ({ participants }) => {
    if (!participants?.length) {
        return _jsx(Typography, { sx: { fontSize: '13px !important', color: '#98A2B3' }, children: "\u2014" });
    }
    return (_jsx(Box, { component: "ol", sx: {
            m: 0,
            p: 0,
            listStyle: 'none',
            counterReset: 'participant',
            // "at most 3 columns, each at least 150px". Container-driven on
            // purpose: the drawer is a fixed-width panel, so MUI's
            // viewport breakpoints would size these off the wrong box.
            columns: '150px 2',
            columnGap: 2,
        }, children: participants.map((email) => (_jsx(Typography, { component: "li", title: email, sx: {
                counterIncrement: 'participant',
                fontSize: '13px !important',
                color: '#475467',
                lineHeight: 1.6,
                // Keeps an entry from being split across two columns.
                breakInside: 'avoid',
                overflowWrap: 'anywhere',
                display: 'flex',
                gap: 0.5,
                '&::before': {
                    content: 'counter(participant) ")"',
                    color: '#98A2B3',
                    flexShrink: 0,
                },
            }, children: email }, email))) }));
};
const Message = ({ children, tone = 'muted', }) => (_jsx(Typography, { sx: {
        fontSize: 13,
        color: tone === 'error' ? '#B42318' : '#667085',
        px: 0.5,
        py: 1.5,
    }, children: children }));
const MeetingAccordionList = ({ meetings, selectedIds, onToggle, loading = false, error = null, idle = false, }) => {
    if (idle) {
        return _jsx(Message, { children: "Select a client to load their recent meetings." });
    }
    if (loading) {
        // `table-loader` switches CommonLoader's container off position:fixed,
        // so the spinner sits inside the drawer instead of centring on the
        // viewport behind it.
        return (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 1 }, children: _jsx(CommonLoader, { className: "table-loader" }) }));
    }
    if (error) {
        return _jsx(Message, { tone: "error", children: error });
    }
    if (!meetings.length) {
        return (_jsx(Message, { children: "No meetings found for this client in the selected date range. Fireflies matches the client name in the meeting title, so a differently-titled meeting won't appear here \u2014 try widening the range. You can still prepare the agenda without one: the performance tables come from the database, and the documents will note what could not be sourced from a call." }));
    }
    return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: meetings.map((m) => {
            const checked = selectedIds.includes(m.transcript_id);
            return (_jsxs(Accordion, { disableGutters: true, elevation: 0, sx: {
                    border: '1px solid #EAECF0',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    bgcolor: checked ? '#FFF5FA' : '#fff',
                }, children: [_jsxs(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, { sx: { color: '#667085' } }), sx: {
                            px: 1.5,
                            '& .MuiAccordionSummary-content': {
                                alignItems: 'center',
                                gap: 1,
                                my: 1,
                                minWidth: 0,
                            },
                        }, children: [_jsx(Checkbox, { checked: checked, onChange: () => onToggle(m.transcript_id), 
                                // Keep ticking the box from toggling the panel.
                                onClick: (e) => e.stopPropagation(), onFocus: (e) => e.stopPropagation(), size: "small", sx: {
                                    p: 0.5,
                                    color: '#D0D5DD',
                                    '&.Mui-checked': { color: '#EE2A7B' },
                                }, inputProps: { 'aria-label': `Select ${m.title}` } }), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { sx: {
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: '#101828',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }, title: m.title, children: m.title || '(untitled meeting)' }), _jsx(Typography, { sx: { fontSize: '12px !important', color: '#667085' }, children: fmtDateTime(m.date) })] })] }), _jsx(AccordionDetails, { sx: { px: 1.5, pt: 0, pb: 1.5 }, children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsxs(Box, { children: [_jsx(SectionLabel, { children: "Key Points" }), _jsx(FormattedMeetingText, { text: m.bullet_gist, emptyText: "No key points captured.", fontSize: "13px" })] }), _jsxs(Box, { children: [_jsx(SectionLabel, { children: "Meeting Summary" }), _jsx(FormattedMeetingText, { text: m.short_summary, emptyText: "No summary available.", fontSize: "13px" })] }), _jsxs(Box, { children: [_jsxs(SectionLabel, { children: ["Participants (", m.participants?.length || 0, ")"] }), _jsx(ParticipantList, { participants: m.participants })] })] }) })] }, m.transcript_id));
        }) }));
};
export default MeetingAccordionList;
